/**
 * Community Image Carousel
 * Displays community cover images and videos with dot indicators
 */

import { Pause, Play } from "lucide-react"
import * as React from "react"
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { cn } from "@/lib"
import type { CarouselMediaItem } from "../types/community-detail"
import { CommunityVideoPlayer } from "./CommunityVideoPlayer"

interface CommunityImageCarouselProps {
  media: CarouselMediaItem[]
  leadingElement?: React.ReactNode
  /** Required when media contains native-video items -- the player mints a
   *  per-video playback token from this community's endpoint. */
  communityId?: string
}

export function CommunityImageCarousel({
  media,
  leadingElement,
  communityId,
}: CommunityImageCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)
  const [playingIndex, setPlayingIndex] = React.useState<number | null>(null)
  const videoRefs = React.useRef<Map<number, HTMLVideoElement>>(new Map())

  React.useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      const newIndex = api.selectedScrollSnap()
      setCurrent(newIndex)
      // Pause video when navigating away
      if (playingIndex !== null && playingIndex !== newIndex) {
        const video = videoRefs.current.get(playingIndex)
        if (video) {
          video.pause()
        }
        setPlayingIndex(null)
      }
    })
  }, [api, playingIndex])

  const placeholderCount = media.length > 0 ? media.length : 4

  const handlePlayVideo = (index: number) => {
    setPlayingIndex(index)
    // Auto-play the video via ref
    const video = videoRefs.current.get(index)
    if (video) {
      video.play().catch(() => {})
    }
  }

  const handlePauseVideo = (index: number) => {
    const video = videoRefs.current.get(index)
    if (video) {
      video.pause()
    }
    setPlayingIndex(null)
  }

  return (
    <div className="w-full relative">
      <Carousel
        setApi={setApi}
        opts={{ watchDrag: media.length > 1 }}
        className="w-full max-w-360 mx-auto"
      >
        {/* Leading Element - positioned at top left inside carousel */}
        {leadingElement && <div className="absolute top-6.25 left-6.25 z-30">{leadingElement}</div>}
        {media.length > 1 && (
          <>
            <CarouselPrevious className="absolute left-5 md:left-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 bg-white/5 backdrop-blur-xs" />
            <CarouselNext className="absolute right-5 md:right-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 bg-white/5 backdrop-blur-xs" />
          </>
        )}
        <CarouselContent>
          {media.length > 0
            ? media.map((item, index) => (
                <CarouselItem key={index}>
                  <div className="max-h-[600px] h-full w-full overflow-hidden relative bg-transparent min-[1445px]:rounded-[30px]">
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={`Community image ${index + 1}`}
                        className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                      />
                    ) : item.type === "native-video" ? (
                      <div className="relative w-full h-[240px] md:h-[600px] min-[1445px]:rounded-[30px] overflow-hidden">
                        {communityId && (
                          <CommunityVideoPlayer
                            communityId={communityId}
                            video={item.video}
                            // Lazy-mint: only the on-screen slide fetches a token.
                            active={index === current}
                            className="min-[1445px]:rounded-[30px]"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="relative w-full h-full md:h-[600px] min-[1445px]:rounded-[30px]">
                        {/* Video element — always rendered, controlled via ref */}
                        <video
                          ref={el => {
                            if (el) videoRefs.current.set(index, el)
                          }}
                          src={item.url}
                          className="w-full h-full object-cover min-[1445px]:rounded-[30px]"
                          playsInline
                          preload="metadata"
                          poster={item.thumbnailUrl || undefined}
                        />

                        {/* Play overlay — show when not playing */}
                        {playingIndex !== index && (
                          <button
                            onClick={() => handlePlayVideo(index)}
                            className="absolute inset-0 flex items-center justify-center z-10 cursor-pointer"
                            aria-label="Play video"
                          >
                            {/* Show thumbnail if available, otherwise dark overlay */}
                            {item.thumbnailUrl && (
                              <img
                                src={item.thumbnailUrl}
                                alt={item.title || "Video thumbnail"}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            )}
                            <div className="absolute inset-0 bg-[rgb(0,0,0,0.4)] z-20" />
                            <Play className="h-10 w-10 text-white z-30 relative" fill="white" />
                          </button>
                        )}

                        {/* Pause overlay — show on top of playing video */}
                        {playingIndex === index && (
                          <button
                            onClick={() => handlePauseVideo(index)}
                            className="absolute inset-0 flex items-center justify-center z-10 cursor-transparent"
                            aria-label="Pause video"
                          >
                            <Pause
                              className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity"
                              fill="white"
                            />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))
            : Array.from({ length: placeholderCount }).map((_, index) => (
                <CarouselItem key={index}>
                  <div className="max-h-[600px] w-full overflow-hidden bg-card" />
                </CarouselItem>
              ))}
        </CarouselContent>
      </Carousel>

      {/* Dot Indicators - only show when multiple slides */}
      {media.length > 1 && (
        <div className="mb-[30px] flex items-center justify-center gap-2 absolute inset-x-0 bottom-0">
          {Array.from({ length: count || placeholderCount }).map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-1 w-8 rounded-none transition-all",
                index === current ? "bg-[#F2F0EB]" : "bg-[#F2F0EB] opacity-20",
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
